import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Search, RefreshCw, TrendingUp, TrendingDown, Plus, AlertTriangle, CheckCircle, AlertCircle, MapPin, Sparkles, Loader2, Zap, ArrowRight, Globe, CheckCircle2, BarChart3, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Acronym } from '../components/shared/AcronymTooltip';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const impactColors = { high: 'bg-red-500', medium: 'bg-amber-500', low: 'bg-blue-500' };
const effortColors = { low: 'bg-emerald-brand', medium: 'bg-amber-500', high: 'bg-red-500' };
const scoreColor = (s) => s >= 80 ? 'text-emerald-600' : s >= 50 ? 'text-amber-600' : 'text-red-600';

const ISSUE_TYPES = ['missing_title', 'missing_meta', 'slow_load', 'broken_links', 'missing_alt', 'duplicate_content', 'no_h1', 'no_sitemap', 'missing_ssl'];

const AuditTab = () => {
  const [pages, setPages] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ url: '', page_title: '', meta_description: '', score: 50, issues: [], notes: '' });
  const [selectedIssues, setSelectedIssues] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [p, d] = await Promise.all([
        axios.get(`${API_URL}/api/seo-audit/pages`),
        axios.get(`${API_URL}/api/seo-audit/dashboard`),
      ]);
      setPages(p.data.pages || []);
      setDashboard(d.data);
    } catch { toast.error('Failed to load audit data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.url.trim()) { toast.error('URL required'); return; }
    try {
      await axios.post(`${API_URL}/api/seo-audit/pages`, { ...form, issues: selectedIssues.map(i => ({ type: i, severity: 'warning' })) });
      toast.success('Page audit added!'); setShowCreate(false); setForm({ url: '', page_title: '', meta_description: '', score: 50, issues: [], notes: '' }); setSelectedIssues([]); fetchData();
    } catch { toast.error('Failed to add audit'); }
  };

  const deletePage = async (id) => { if (!window.confirm('Delete this audit?')) return; try { await axios.delete(`${API_URL}/api/seo-audit/pages/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed to delete'); } };

  const toggleIssue = (issue) => setSelectedIssues(prev => prev.includes(issue) ? prev.filter(i => i !== issue) : [...prev, issue]);

  const filtered = pages.filter(p => !search || p.url?.toLowerCase().includes(search.toLowerCase()) || p.page_title?.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-4" data-testid="seo-audit-tab">
      <div className="flex justify-end">
        <Button onClick={() => setShowCreate(true)} data-testid="add-audit-btn"><Plus className="h-4 w-4 mr-1" />Add Page Audit</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-white border-gray-border"><CardContent className="p-3 text-center"><Globe className="h-5 w-5 mx-auto text-blue-500 mb-1" /><p className="text-xl font-bold text-text-primary">{dashboard.total_pages || 0}</p><p className="text-[10px] text-gray-medium">Total Pages</p></CardContent></Card>
        <Card className="bg-white border-gray-border"><CardContent className="p-3 text-center"><BarChart3 className="h-5 w-5 mx-auto text-purple-500 mb-1" /><p className="text-xl font-bold text-text-primary">{dashboard.avg_score || 0}</p><p className="text-[10px] text-gray-medium">Avg Score</p></CardContent></Card>
        <Card className="bg-white border-gray-border"><CardContent className="p-3 text-center"><CheckCircle2 className="h-5 w-5 mx-auto text-emerald-500 mb-1" /><p className="text-xl font-bold text-text-primary">{dashboard.good_pages || 0}</p><p className="text-[10px] text-gray-medium">Good</p></CardContent></Card>
        <Card className="bg-white border-gray-border"><CardContent className="p-3 text-center"><AlertTriangle className="h-5 w-5 mx-auto text-red-500 mb-1" /><p className="text-xl font-bold text-text-primary">{dashboard.critical_pages || 0}</p><p className="text-[10px] text-gray-medium">Critical</p></CardContent></Card>
      </div>
      {dashboard.top_issues?.length > 0 && (
        <Card className="bg-white border-gray-border"><CardContent className="p-3"><p className="text-xs font-medium text-gray-medium mb-2">Top Issues</p><div className="flex flex-wrap gap-2">{dashboard.top_issues.map(i => <Badge key={i.type} className="text-xs bg-red-50 text-red-600">{i.type.replace(/_/g, ' ')} ({i.count})</Badge>)}</div></CardContent></Card>
      )}
      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /><Input placeholder="Search pages..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="audit-search" /></div></CardHeader>
        <CardContent>{filtered.length === 0 ? (<div className="text-center py-12 text-gray-medium"><Globe className="h-10 w-10 mx-auto mb-3 opacity-30" /><p>No audited pages yet</p></div>) : (
          <div className="space-y-2">{filtered.map(p => (
            <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border hover:bg-gray-50" data-testid={`audit-row-${p.id}`}>
              <div className="w-12 text-center flex-shrink-0"><span className={cn("text-lg font-bold", scoreColor(p.score))}>{p.score}</span></div>
              <div className="flex-1 min-w-0"><span className="font-medium text-sm text-text-primary truncate block">{p.page_title || p.url}</span><p className="text-xs text-gray-medium truncate">{p.url}</p></div>
              <div className="w-20 flex-shrink-0"><Progress value={p.score} className="h-1.5" /></div>
              {p.issues?.length > 0 && <Badge className="text-[10px] bg-red-50 text-red-600">{p.issues.length} issues</Badge>}
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deletePage(p.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
            </div>
          ))}</div>
        )}</CardContent>
      </Card>
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Add Page Audit</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Page URL *</Label><Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} data-testid="audit-url" /></div>
            <div><Label>Page Title</Label><Input value={form.page_title} onChange={e => setForm(f => ({ ...f, page_title: e.target.value }))} /></div>
            <div><Label>Meta Description</Label><Textarea value={form.meta_description} onChange={e => setForm(f => ({ ...f, meta_description: e.target.value }))} rows={2} /></div>
            <div><Label>SEO Score (0-100)</Label><Input type="number" min={0} max={100} value={form.score} onChange={e => setForm(f => ({ ...f, score: Number(e.target.value) }))} /></div>
            <div><Label>Issues</Label><div className="flex flex-wrap gap-1.5 mt-1">{ISSUE_TYPES.map(i => <Badge key={i} className={cn("text-xs cursor-pointer", selectedIssues.includes(i) ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-500 hover:bg-gray-200")} onClick={() => toggleIssue(i)}>{i.replace(/_/g, ' ')}</Badge>)}</div></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} data-testid="save-audit-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const QuickWinsTab = () => {
  const [quickWins, setQuickWins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQuickWins = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/seo/quick-wins`);
        setQuickWins(res.data.quick_wins || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchQuickWins();
  }, []);

  const markDone = (id) => {
    setQuickWins(prev => prev.map(w => w.id === id ? { ...w, status: 'done' } : w));
    toast.success('Quick win marked as done!');
  };

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-8 w-8 animate-spin text-emerald-brand" /></div>;

  return (
    <div className="space-y-4" data-testid="seo-quick-wins">
      <Card className="bg-emerald-brand/5 border-emerald-brand/20">
        <CardContent className="p-4 flex items-center gap-3">
          <Zap className="h-5 w-5 text-emerald-brand flex-shrink-0" />
          <div>
            <p className="font-medium text-text-primary">AI-Powered Quick Wins</p>
            <p className="text-sm text-gray-medium">High-impact, low-effort SEO improvements you can make today</p>
          </div>
        </CardContent>
      </Card>
      {quickWins.map((win) => (
        <Card key={win.id} className={cn("bg-white border-gray-border transition-opacity", win.status === 'done' && "opacity-50")} data-testid={`quick-win-${win.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-xs">{win.category}</Badge>
                  <Badge className={cn("text-white text-xs capitalize", impactColors[win.impact])}>{win.impact} impact</Badge>
                  <Badge className={cn("text-white text-xs capitalize", effortColors[win.effort])}>{win.effort} effort</Badge>
                </div>
                <h3 className="font-medium text-text-primary mb-1">{win.title}</h3>
                <p className="text-sm text-gray-medium">{win.description}</p>
              </div>
              <Button
                size="sm"
                variant={win.status === 'done' ? 'outline' : 'default'}
                className={win.status !== 'done' ? 'bg-emerald-brand hover:bg-emerald-hover' : ''}
                onClick={() => markDone(win.id)}
                disabled={win.status === 'done'}
                data-testid={`quick-win-done-${win.id}`}
              >
                {win.status === 'done' ? <CheckCircle className="h-4 w-4 mr-1" /> : <ArrowRight className="h-4 w-4 mr-1" />}
                {win.status === 'done' ? 'Done' : 'Fix Now'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export const SEOPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [technical, setTechnical] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newKeyword, setNewKeyword] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, keywordsRes, technicalRes] = await Promise.all([
        axios.get(`${API_URL}/api/seo/dashboard`),
        axios.get(`${API_URL}/api/seo/keywords`),
        axios.get(`${API_URL}/api/seo/technical`),
      ]);
      setDashboard(dashRes.data);
      setKeywords(keywordsRes.data.keywords || []);
      setTechnical(technicalRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addKeyword = async () => {
    if (!newKeyword) return;
    try {
      await axios.post(`${API_URL}/api/seo/keywords`, null, {
        params: { keyword: newKeyword },
        
      });
      setNewKeyword('');
      fetchData();
    } catch (error) {
      console.error('Add keyword error:', error);
    }
  };

  const generateKeywordIdeas = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API_URL}/api/ai/keyword-ideas`, {
        business_description: 'Small business marketing',
        location: 'United States'
      });
      // Normally would display these suggestions
    } catch (error) {
      console.error('AI generation error:', error);
    } finally {
      setGenerating(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 71) return 'text-emerald-brand';
    if (score >= 41) return 'text-amber-500';
    return 'text-red-500';
  };

  const severityConfig = {
    critical: { icon: AlertTriangle, color: 'text-red-500 bg-red-50' },
    warning: { icon: AlertCircle, color: 'text-amber-500 bg-amber-50' },
    info: { icon: CheckCircle, color: 'text-blue-500 bg-blue-50' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-emerald-brand animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="seo-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary"><Acronym term="SEO" /> Center<PageInfoTooltip pageKey="seo-center" /></h1>
        <p className="text-gray-medium">Optimize your search engine visibility</p>
      </div>

      {/* Health Score & Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        <Card className="col-span-2 md:col-span-1 bg-white border-gray-border">
          <CardContent className="p-4 sm:p-5 text-center">
            <div className={cn("text-3xl sm:text-4xl font-heading font-bold mb-2", getScoreColor(dashboard?.health_score || 0))}>
              {dashboard?.health_score || 0}
            </div>
            <Progress value={dashboard?.health_score || 0} className="mb-2" />
            <p className="text-xs sm:text-sm text-gray-medium">SEO Health Score</p>
          </CardContent>
        </Card>

        {[
          { label: 'Top 3', value: dashboard?.keywords_top_3 || 0, color: 'text-emerald-brand' },
          { label: 'Top 10', value: dashboard?.keywords_top_10 || 0, color: 'text-blue-500' },
          { label: 'Top 20', value: dashboard?.keywords_top_20 || 0, color: 'text-amber-500' },
          { label: 'Not Ranking', value: dashboard?.keywords_not_ranking || 0, color: 'text-red-500' },
        ].map((stat, i) => (
          <Card key={i} className="bg-white border-gray-border">
            <CardContent className="p-3 sm:p-4 text-center">
              <div className={cn("text-lg sm:text-2xl font-bold", stat.color)}>{stat.value}</div>
              <p className="text-[10px] sm:text-xs text-gray-medium">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="keywords" className="space-y-6">
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="keywords" className="text-xs sm:text-sm whitespace-nowrap">Keywords</TabsTrigger>
          <TabsTrigger value="quick-wins" data-testid="tab-quick-wins" className="text-xs sm:text-sm whitespace-nowrap">Quick Wins</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit" className="text-xs sm:text-sm whitespace-nowrap">Page Audit</TabsTrigger>
          <TabsTrigger value="technical" className="text-xs sm:text-sm whitespace-nowrap">Technical SEO</TabsTrigger>
          <TabsTrigger value="local" className="text-xs sm:text-sm whitespace-nowrap">Local SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="keywords" className="space-y-4">
          {/* Add Keyword */}
          <Card className="bg-white border-gray-border">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  placeholder="Enter keyword to track..."
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={addKeyword} className="bg-emerald-brand hover:bg-emerald-hover">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Keyword
                </Button>
                <Button variant="outline" onClick={generateKeywordIdeas} disabled={generating}>
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                  AI Suggest
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Keywords Table */}
          <Card className="bg-white border-gray-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Tracked Keywords</CardTitle>
            </CardHeader>
            <CardContent>
              {keywords.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-border">
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-medium">Keyword</th>
                        <th className="text-center py-3 px-2 text-sm font-medium text-gray-medium">Position</th>
                        <th className="text-center py-3 px-2 text-sm font-medium text-gray-medium">Change</th>
                        <th className="text-center py-3 px-2 text-sm font-medium text-gray-medium">Volume</th>
                        <th className="text-center py-3 px-2 text-sm font-medium text-gray-medium">Difficulty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keywords.map((kw, i) => (
                        <tr key={i} className="border-b border-gray-border last:border-0">
                          <td className="py-3 px-2 font-medium text-text-primary">{kw.keyword}</td>
                          <td className="py-3 px-2 text-center">{kw.position || '-'}</td>
                          <td className="py-3 px-2 text-center">
                            <span className={cn("flex items-center justify-center gap-1", kw.change >= 0 ? 'text-emerald-brand' : 'text-red-500')}>
                              {kw.change >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                              {Math.abs(kw.change || 0)}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center text-gray-medium">{kw.search_volume || '-'}</td>
                          <td className="py-3 px-2 text-center">
                            <Badge variant="secondary">{kw.difficulty || 'Medium'}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-medium">No keywords tracked yet</p>
                  <p className="text-sm text-gray-medium">Add keywords above to start tracking</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quick-wins" className="space-y-4">
          <QuickWinsTab />
        </TabsContent>

        <TabsContent value="audit" className="space-y-4">
          <AuditTab />
        </TabsContent>

        <TabsContent value="technical" className="space-y-4">
          <Card className="bg-white border-gray-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Technical Issues</CardTitle>
            </CardHeader>
            <CardContent>
              {technical?.issues?.length > 0 ? (
                <div className="space-y-3">
                  {technical.issues.map((issue, i) => {
                    const config = severityConfig[issue.severity] || severityConfig.info;
                    const Icon = config.icon;
                    return (
                      <div key={i} className={cn("flex items-start gap-3 p-3 rounded-lg", config.color)}>
                        <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-medium text-text-primary">{issue.issue}</p>
                          <p className="text-sm text-gray-medium">{issue.affected_pages} pages affected</p>
                        </div>
                        <Button size="sm" variant="outline">Fix</Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-emerald-brand" />
                  <p className="text-emerald-brand font-medium">No technical issues found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="local" className="space-y-4">
          <Card className="bg-white border-gray-border">
            <CardContent className="p-8 text-center">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-medium">Local SEO features coming soon</p>
              <p className="text-sm text-gray-medium">Google Business Profile integration, NAP consistency, and more</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SEOPage;
